import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { uatHost, uatTransport, type UatView } from '../lib/sb1-uat';
import DepartmentCatalog from '../components/DepartmentCatalog';
import FinanceStatement from '../components/FinanceStatement';
import ArAging from '../components/ArAging';
import ReportExportMenu from '../components/ReportExportMenu';
import { createDepartmentOptionsLoader, type DepartmentOptionsResult } from '../lib/department-options';
import Growth from './Growth';
import { createCatalogLoader, type CatalogResult, type CatalogTransport } from '../lib/warehouse-catalog-client';
import { createIncomeStatementLoader, createDepartmentIncomeLoader, type Currency, type StatementResult } from '../lib/live-income-statement';
import { createBalanceSheetLoader } from '../lib/live-balance-sheet';
import { parsePreviewRoute } from '../lib/warehouse-preview-route';
import '../components/department-catalog.css';
import '../components/finance-statement.css';
import '../components/growth.css';

const transport: CatalogTransport = (path, init) => fetch(path, init);
const SandboxViewingAs = import.meta.env.VITE_SB1_UAT === 'true'
  ? lazy(() => import('../components/SandboxViewingAs')) : null;

function LoadedStatement({ currency, asOf, department, transport: request, sandbox }: { currency: Currency; asOf?: string; department?: string; transport: CatalogTransport; sandbox: boolean }) {
  const [result, setResult] = useState<StatementResult>({ state: 'loading' });
  useEffect(() => {
    if (department) {
      const loader = createDepartmentIncomeLoader(request, setResult);
      void loader.refresh(currency, department);
      return () => loader.dispose();
    }
    if (asOf) {
      const loader = createBalanceSheetLoader(request, setResult);
      void loader.refresh(currency, asOf);
      return () => loader.dispose();
    }
    const loader = createIncomeStatementLoader(request, setResult);
    void loader.refresh(currency);
    return () => loader.dispose();
  }, [currency, asOf, department, request]);
  if (result.state === 'ready') return <FinanceStatement {...result.data} transactionTransport={request} departmentId={department} liveSource={sandbox ? {
    ...result.data.liveSource, label: 'Sandbox Data Only · NetSuite SB1 via Finance API',
    note: `Sandbox Data Only. ${result.data.liveSource.note} Read directly from NetSuite SB1, not the warehouse. UAT only; no production data validation is implied.`,
  } : result.data.liveSource} reconciliations={[]} />;
  const messages = {
    loading: ['Loading statement…', 'No previous figures are retained while the request is pending.'],
    unauthenticated: ['Sign-in required', 'Your report session could not be authenticated. Sign in through dev-finance, then refresh access and data.'],
    denied: ['Report access not granted', 'The API denied this request. No Finance figures are retained. Contact the access owner.'],
    error: ['Statement unavailable', 'The request failed or the response was incomplete or unsupported. No zero statement or snapshot has been substituted. Use Refresh access and data to retry.'],
  };
  return <div className="wc-state" role={result.state === 'error' ? 'alert' : 'status'}>
    <ReportExportMenu />
    <h2>{messages[result.state][0]}</h2><p>{messages[result.state][1]}</p>
  </div>;
}

function FinanceReport({ id, title, transport: request, sandbox }: { id: string; title: string; transport: CatalogTransport; sandbox: boolean }) {
  const [currency, setCurrency] = useState<Currency>('USD');
  const [date, setDate] = useState('');
  const [asOf, setAsOf] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  if (id === 'ar-aging') return <ArAging transport={request} sandbox={sandbox} />;
  if (id === 'department-income-statement') return <>
    <div className="fs-filters"><label>Subsidiary base currency <select aria-label="Subsidiary base currency" value={currency} onChange={(event) => setCurrency(event.target.value as Currency)}>
      <option value="USD">USD</option><option value="GBP">GBP</option>
    </select></label></div>
    <DepartmentPnl currency={currency} transport={request} sandbox={sandbox} />
  </>;
  if (!['income-statement', 'balance-sheet', 'department-income-statement'].includes(id)) return <div className="wc-state" role="status">
    <ReportExportMenu unavailableReason="This report screen and its export adapter are not connected yet." />
    <h2>{title}: screen not connected yet</h2>
    <p>The API catalog permits this report, but its new-design screen is not implemented. No fixture or legacy report is substituted.</p>
  </div>;
  return <>
    <div className="fs-filters"><label>Subsidiary base currency <select aria-label="Subsidiary base currency" value={currency} onChange={(event) => setCurrency(event.target.value as Currency)}>
      <option value="USD">USD</option><option value="GBP">GBP</option>
    </select></label></div>
    {id === 'balance-sheet' && <form className="fs-filters" onSubmit={(event) => { event.preventDefault(); setAsOf(date); setLoadAttempt((n) => n + 1); }}>
      <label>Accounting period end <input type="date" aria-label="Accounting period end" required value={date}
        onChange={(event) => { setDate(event.target.value); setAsOf(''); }} /></label>
      <button type="submit">Load balance sheet</button>
      <p>Enter an accounting period-end date. The API validates it against the source calendar; no date or closed status is assumed.</p>
    </form>}
    {id === 'balance-sheet' && !asOf ? <div className="wc-state" role="status"><ReportExportMenu />
      <p>Enter the required report scope to load the statement. No previous figures are retained.</p></div>
      : <LoadedStatement key={`${currency}:${asOf}:${loadAttempt}`} currency={currency} asOf={id === 'balance-sheet' ? asOf : undefined} transport={request} sandbox={sandbox} />}
  </>;
}

function DepartmentPnl({ currency, transport: request, sandbox }: { currency: Currency; transport: CatalogTransport; sandbox: boolean }) {
  const [options, setOptions] = useState<DepartmentOptionsResult>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [department, setDepartment] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  useEffect(() => {
    const loader = createDepartmentOptionsLoader(request, setOptions);
    void loader.refresh();
    return () => loader.dispose();
  }, [attempt, request]);

  const retryOptions = () => { setDepartment(''); setOptions({ state: 'loading' }); setAttempt((n) => n + 1); };
  return <>
    <div className="fs-filters">
      <label>Department <select aria-label="Department" required value={department}
        disabled={options.state !== 'ready' || options.data.departments_v1.length === 0}
        onChange={(event) => { setDepartment(event.target.value); setLoadAttempt((n) => n + 1); }}>
        <option value="">{options.state === 'loading' ? 'Loading authorized departments…' : 'Select a department'}</option>
        {options.state === 'ready' && options.data.departments_v1.map((item) => <option value={item.dept_id} key={item.dept_id}>
          {item.name}{item.active ? '' : ' (inactive)'}
        </option>)}
      </select></label>
      <p>Only departments in your approved NetSuite access scope are listed. Selecting one loads its P&amp;L.</p>
    </div>
    {options.state === 'unauthenticated' && <div className="wc-state" role="status"><h2>Sign-in required</h2><p>Your report session could not be authenticated.</p></div>}
    {options.state === 'denied' && <div className="wc-state" role="alert"><h2>Department access not granted</h2><p>The server did not authorize access to the department catalog. No department names or report figures are shown.</p></div>}
    {options.state === 'error' && <div className="wc-state" role="alert"><h2>Department list unavailable</h2><p>NetSuite departments could not be loaded. No department P&amp;L is shown.</p><button type="button" className="wc-retry" onClick={retryOptions}>Try again</button></div>}
    {options.state === 'ready' && options.data.departments_v1.length === 0 && <div className="wc-state" role="status"><h2>No departments available</h2><p>Your current access scope contains no departments in NetSuite.</p></div>}
    {options.state === 'ready' && !!department && <LoadedStatement key={`${currency}:${department}:${loadAttempt}`} currency={currency} department={department} transport={request} sandbox={sandbox} />}
  </>;
}

/** No legacy dashboard, diagnostic probes, fixture imports or client-side grants. */
export default function Warehouse() {
  const sandboxBuild = SandboxViewingAs !== null && uatHost(window.location.hostname);
  const blockedHost = SandboxViewingAs !== null && !uatHost(window.location.hostname);
  const selectionGeneration = useRef(0);
  const [view, setView] = useState<UatView>({ kind: 'actual' });
  const request = useMemo(() => sandboxBuild ? uatTransport(transport, view) : transport, [sandboxBuild, view]);
  const [catalog, setCatalog] = useState<CatalogResult>({ state: 'loading' });
  const [epoch, setEpoch] = useState(0);
  const [route, setRoute] = useState(() => parsePreviewRoute(window.location.hash));
  useEffect(() => {
    const update = () => setRoute(parsePreviewRoute(window.location.hash));
    window.addEventListener('hashchange', update);
    return () => window.removeEventListener('hashchange', update);
  }, []);
  useEffect(() => {
    if (blockedHost) { setCatalog({ state: 'error' }); return; }
    const generation = selectionGeneration.current;
    const publish = (next: CatalogResult) => {
      if (selectionGeneration.current !== generation) return;
      if (sandboxBuild && next.state === 'ready' && (!next.uat || JSON.stringify(next.uat.view) !== JSON.stringify(view))) {
        setCatalog({ state: 'error' });
      } else setCatalog(next);
    };
    // Load once when entering a view. Focus/visibility/timer refreshes used to
    // replace the page while Finance was reviewing it. The explicit sidebar
    // action remains available when a user wants fresh access and catalog data.
    const loader = createCatalogLoader(request, publish);
    void loader.refresh();
    return () => loader.dispose();
  }, [epoch, request, sandboxBuild, view, blockedHost]);
  const refresh = () => { setCatalog({ state: 'loading' }); setEpoch((n) => n + 1); };
  if (blockedHost) return <main><h1>SB1 test build unavailable on this host</h1><p>No catalog or report request was made. Use the approved dev-finance host.</p></main>;
  return <DepartmentCatalog mode="live" catalog={catalog} selectedDepartment={route.department}
    selectedReport={route.report} selectedPage={route.growth ? 'growth' : undefined} growthPage={<Growth />}
    invalidRoute={route.invalid || window.location.pathname !== '/'}
    onRetry={refresh} buildRef={import.meta.env.VITE_BUILD_SHA || 'local build'}
    testControls={sandboxBuild && (catalog.state !== 'ready'
      ? <p className="sb1-controls">SB1 test build · Sandbox Data Only verification pending; no figures available.</p>
      : SandboxViewingAs && <Suspense fallback={<p>Loading test controls…</p>}><SandboxViewingAs value={view} onChange={(next) => {
        selectionGeneration.current++; setCatalog({ state: 'loading' }); setView(next);
      }} /></Suspense>)}
    renderReport={(report) => <FinanceReport key={`${catalog.policyRevision}:${JSON.stringify(view)}:${report.id}`} {...report} transport={request} sandbox={sandboxBuild} />} />;
}
